import { useMutation, useQuery } from 'react-apollo';
import produce from 'immer';

import useFileUpload from '../../../../../../apps/pro/redux/fileUpload/useFileUpload';
import {
  photoBatchCreate as photoBatchCreateMutation,
  photoBatchPhotosUpdate as photoBatchPhotosUpdateMutation,
  photoBatchPhotosUpload as photoBatchPhotosUploadMutation,
} from '../../../../../../graphql/mutations/photos';
import { AccountPhotoManagerSubscriptions } from '../../../../../../graphql/queries/account';
import { useUploadPermission } from '../../../../../../apps/pro/services/contributors';
import { albumAddPhotos as albumAddPhotosMutation } from '../../../../../../graphql/mutations/albums';
import { AlbumForUpload } from '../../../../../../apps/pro/views/AlbumFolder/AlbumFolderUpload/components/AlbumFolderUploadDestination/graphql';
import {
  AlbumSlideshow as AlbumSlideshowQuery,
  AlbumSlideshowForContributors as AlbumSlideshowForContributorsQuery,
  AlbumSlideshowForPolling,
} from '../../../../../../graphql/queries/albums';
import { ALBUM_SLIDESHOW_MODE } from '../../../../../../constants/albums';
import useAlbumSlideshow from '../../../../../../apps/pro/views/Album/AlbumSlideshow/hooks/useAlbumSlideshow';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../constants/contributors';
import { autoContributorJoin as autoContributorJoinMutation } from '../../../../../../graphql/mutations/contributors';
import { ContributorPermissions } from '../../../../../../apps/pro/services/contributors/hooks/graphql/queries';

const useSlideUploader = ({ albumId }) => {
  const { data: { me } = {} } = useQuery(AccountPhotoManagerSubscriptions, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: { album } = {} } = useQuery(AlbumForUpload, {
    variables: {
      id: albumId,
    },
    fetchPolicy: 'cache-and-network',
    skip: !albumId,
  });

  const {
    slideshow,
    onSlideshowUpdate,
    onSlideshowContributeSend,
  } = useAlbumSlideshow({ albumId });
  const {
    isOwner,
    isAllowed,
    isContributor,
    contributorAssignment,
  } = useUploadPermission({
    entityId: albumId,
  });

  const {
    files,
    complete,
    completed,
    uploadReset,
    photosUpload,
  } = useFileUpload();

  const [photoBatchCreate] = useMutation(photoBatchCreateMutation);
  const [photoBatchPhotosUpload] = useMutation(photoBatchPhotosUploadMutation);
  const [photoBatchPhotosUpdate] = useMutation(photoBatchPhotosUpdateMutation);
  const [autoContributorJoin] = useMutation(autoContributorJoinMutation);

  // Upload
  const [albumAddPhotos] = useMutation(albumAddPhotosMutation);

  const onPhotosBatchCreate = async photosCount => {
    const result = await photoBatchCreate({
      variables: {
        input: {
          photos_count: photosCount,
        },
      },
    });
    return result;
  };

  const onPhotoBatchPhotosUpload = async ({ photoBatchId, photos }) => {
    const result = await photoBatchPhotosUpload({
      variables: {
        input: {
          photo_batch_id: photoBatchId,
          photos,
        },
      },
    });
    return result;
  };

  const onPhotoBatchPhotosUpdate = async ({
    photoBatchPhotoId,
    uploadStatus,
    uploadStartDate,
    uploadEndDate,
  }) => {
    const result = await photoBatchPhotosUpdate({
      variables: {
        input: {
          photo_batch_photos: [
            {
              photo_batch_photo_id: photoBatchPhotoId,
              upload_status: uploadStatus,
              upload_start_date: uploadStartDate,
              upload_end_date: uploadEndDate,
            },
          ],
        },
      },
    });
    return result;
  };

  const onAlbumAddPhotos = async (photoIds, queryOptions = {}) => {
    const result = await albumAddPhotos({
      variables: {
        input: {
          album_id: albumId,
          photo_ids: photoIds,
        },
      },
      ...queryOptions,
    });
    return result;
  };

  const onPhotosUpload = async photos => {
    const result = await photosUpload({
      photos,
      photoBatchPhotosUploadMutation: onPhotoBatchPhotosUpload,
      photoBatchPhotosUpdateMutation: onPhotoBatchPhotosUpdate,
      photoBatchCreateMutation: onPhotosBatchCreate,
    });

    return result;
  };

  const onSlidesCreate = async () => {
    const photoIds = completed.map(({ photoId }) => photoId);

    // As an owner, uploaded photos do not get added automatically to the slideshow.
    // Because of this, owner uploads must create the slideshow based off the mode.
    if (isOwner && slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS) {
      await onSlideshowUpdate({
        createSelectedPhotos: photoIds.map(photoId => ({ photo_id: photoId })),
      });
    }

    await onAlbumAddPhotos(photoIds, {
      refetchQueries:
        slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
          ? [
              {
                query: AlbumSlideshowForPolling,
                variables: { id: albumId },
              },
            ]
          : [],
      update:
        slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
          ? undefined
          : store => {
              const albumSlideshowPhotos = photoIds.map(photoId => ({
                photo: {
                  id: photoId,
                  description: null,
                  thumbnail_urls: null,
                  __typename: 'Photo',
                },
                display_order: null,
                __typename: 'AlbumSlideshowPhoto',
              }));

              if (isOwner) {
                try {
                  const data = store.readQuery({
                    query: AlbumSlideshowQuery,
                    variables: { id: albumId },
                  });

                  const updatedData = produce(data, draft => {
                    draft.album = {
                      ...draft.album,
                      slideshow: {
                        ...draft.album.slideshow,
                        duration:
                          draft.album.slideshow.duration +
                          (photoIds.length *
                            draft.album.slideshow.slide_duration_ms) /
                            1000,
                        photos: {
                          ...draft.album.slideshow.photos,
                          total:
                            draft.album.slideshow.photos.total +
                            photoIds.length,
                          records: [
                            ...albumSlideshowPhotos,
                            ...draft.album.slideshow.photos.records,
                          ],
                        },
                      },
                    };
                  });

                  store.writeQuery({
                    query: AlbumSlideshowQuery,
                    variables: { id: albumId },
                    data: updatedData,
                  });
                } catch {
                  // Nothing to do here. Queries might not be loaded.
                }
                //  Only care for updating contributor's query if and only if
                //  the contributor is allowed to publish without review
              } else if (
                contributorAssignment?.upload_access ===
                CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH
              ) {
                try {
                  const data = store.readQuery({
                    query: AlbumSlideshowForContributorsQuery,
                    variables: { id: albumId },
                  });

                  const updatedData = produce(data, draft => {
                    draft.album = {
                      ...draft.album,
                      slideshow: {
                        ...draft.album.slideshow,
                        duration:
                          draft.album.slideshow.duration +
                          (photoIds.length *
                            draft.album.slideshow.slide_duration_ms) /
                            1000,
                        photos: {
                          ...draft.album.slideshow.photos,
                          total:
                            draft.album.slideshow.photos.total +
                            photoIds.length,
                          records: [
                            ...albumSlideshowPhotos,
                            ...draft.album.slideshow.photos.records,
                          ],
                        },
                      },
                    };
                  });

                  store.writeQuery({
                    query: AlbumSlideshowForContributorsQuery,
                    variables: { id: albumId },
                    data: updatedData,
                  });
                } catch {
                  // Nothing to do here. Queries might not be loaded.
                }
              }
            },
    });
  };

  const onAutoContributorJoin = async entityId => {
    await autoContributorJoin({
      variables: {
        input: {
          id: entityId,
        },
      },
      // Refetch the query since we will need the newly created contributorAssignment
      refetchQueries: [
        {
          query: ContributorPermissions,
          variables: {
            id: entityId,
          },
        },
      ],
    });
  };

  return {
    user: me,
    isAllowed,
    isContributor,
    contributorAssignment,
    album,
    albumFolder: album?.album_folder,
    files,
    complete,
    uploadReset,
    onPhotosUpload,
    onSlidesCreate,
    onSlideshowContributeSend,
    onAutoContributorJoin,
  };
};

export default useSlideUploader;
